import type React from 'react';

import type { Client, RuleClientPriority } from '@/app/models/client';
import { useSearchClientQuery } from '@/app/services/clientApi';
import { useCreateOrUpdateClientPriorityMutation } from '@/app/services/clientPriorityApi';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DrawerButtons from '../common/DrawerButtons';
import GenericAutoComplete from '../common/GenericAutoComplete';

interface PriorityProjectProps {
  rule: RuleClientPriority | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditPriorityProject(props: PriorityProjectProps) {
  const { rule: ruleProject, setDrawerOpen } = props;
  const { t } = useTranslation(['projectPriority', 'common']);
  const theme = useTheme();

  const [selectedPriority, setSelectedPriority] = useState<number | null>(ruleProject?.priority || null);
  const [clientSearch, setClientSearch] = useState(ruleProject?.client.name || '');
  const [client, setClient] = useState<Client | null>(ruleProject?.client || null);

  const { data: clients, isFetching: isLoadingClients } = useSearchClientQuery(
    {
      searchTerm: clientSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: clientSearch.length < 1 },
  );

  const [addOrUpdateClientPriority] = useCreateOrUpdateClientPriorityMutation();

  const handleSubmit = async () => {
    if (!selectedPriority || !client) return;

    setDrawerOpen(false);
    toast.info(
      t('common:processing.wait.a.moment', {
        position: toast.POSITION.TOP_RIGHT,
      }),
    );

    const response = await addOrUpdateClientPriority({ priority: selectedPriority, clientCode: client?.code });

    if ('data' in response) {
      toast.dismiss();
      toast.success(
        t('add.client.priority.success', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    } else {
      toast.error(
        t('common:processing.failed', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 1,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle
          title={t('title.new.rule.priority.project')}
          subtitle={t('new.rule.priority.project.message') as string}
        />

        <Box sx={{ mb: 1.75 }} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ display: 'flex' }} variant="fieldHeader">
            {t('client')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            {!ruleProject && (
              <GenericAutoComplete
                value={client}
                onChange={(value) => {
                  if (value) setClient(value);
                }}
                equalityTest={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) => `${option?.name}`}
                onSearch={(value) => {
                  setClientSearch(value);
                }}
                options={clientSearch !== '' && clients?.content ? clients?.content : []}
                placeholder={t('enter.client.name') || ''}
                loading={isLoadingClients}
                sx={{ width: theme.spacing(50) }}
              />
            )}

            {ruleProject && (
              <TextField value={client?.code} variant="outlined" size="small" disabled sx={{ width: '100%' }} />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ display: 'flex' }} variant="fieldHeader">
            {t('priority')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              mb: 1,
            }}
          >
            <TextField
              id="outlined-number"
              type="number"
              size="small"
              variant="outlined"
              required
              inputProps={{ min: 1, max: 100 }}
              value={selectedPriority}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value) {
                  setSelectedPriority(Number(event.target.value));
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          mb: 1,
          mt: 'auto',
        }}
      >
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={() => setDrawerOpen(false)}
          disabled={!selectedPriority || selectedPriority === 0 || !client}
        />
      </Box>
    </Box>
  );
}
